/**
 * useRoleView – Central composable for role-based visibility in the case detail view.
 *
 * Maps the user's organizationRoles (from authStore) to a normalized KARS role
 * and provides reactive visibility flags, field configs, and CTA logic.
 */
import { computed } from "vue";
import { useAuthStore } from "@/stores/authStore";
import { gtKey } from "@/i18n/gt";

// ─── Normalized KARS roles ──────────────────────────────────
export type KarsRole =
  | "autohaus"
  | "mietwagen"
  | "sachverstaendiger"
  | "anwalt"
  | "mandant"
  | "abschleppdienst"
  | "versicherung"
  // Inhaber-Feedback 15.08.26, II.11: Fuhrpark (wie Firmenkunde/Autohaus-Kunde:
  // eigene Fahrzeuge, Werkstattrechnungen, keine Sachverständigenkosten) und
  // Versicherungsmakler (wie Versicherung, ohne Regulierungs-/Zahlungsrechte).
  | "fuhrpark"
  | "versicherungsmakler"
  | "other";

/**
 * Reihenfolge = Priorität. Die erste vorhandene Rolle ist die Hauptrolle
 * (Beschriftungen, Einzelwert-Konsumenten); alle vorhandenen Rollen werden
 * für Sichtbarkeit und Konfiguration vereinigt (Design Mehrfachrollen
 * 11.09.2026: Ein Autohaus mit SV-Abteilung sieht beides).
 */
export const ROLE_PRIORITY: readonly KarsRole[] = [
  "anwalt", "sachverstaendiger", "autohaus", "fuhrpark", "mietwagen", "abschleppdienst",
  "versicherung", "versicherungsmakler", "mandant",
] as const;

/** Rohe `organizationRoles` aus dem authStore → normalisierte KARS-Rolle. */
export const ORG_ROLE_TO_KARS: Readonly<Record<string, KarsRole>> = {
  Rechtsanwalt: "anwalt",
  "Sachverständiger": "sachverstaendiger",
  Autohaus: "autohaus",
  Autovermietung: "mietwagen",
  Abschleppdienst: "abschleppdienst",
  Versicherung: "versicherung",
  // Inhaber-Feedback 15.08.26, II.11: Fuhrpark und Versicherungsmakler haben
  // eigene, engere Sichten (COCKPIT_TILES_BY_ROLE & Co.). Im Backend fallen
  // sie auf die Rechteklassen Werkstatt bzw. Versicherung
  // (form.classifyRole).
  Fuhrpark: "fuhrpark",
  Versicherungsmakler: "versicherungsmakler",
  Privatkunde: "mandant",
  Zeuge: "mandant",
  Firmenkunde: "mandant",
} as const;

/**
 * Rollen, die den Fall nicht führen: Mandanten sowie Logistik-/Regulierungs-
 * Beteiligte. Eine zusätzliche fallführende Rolle schaltet die entsprechenden
 * Rechte wieder frei.
 */
const NON_CASE_LEADING_ROLES: KarsRole[] = [
  "mandant", "fuhrpark", "abschleppdienst", "versicherung", "versicherungsmakler",
];

/** True, sobald der Nutzer mindestens eine fallführende Rolle besitzt. */
const hasCaseLeadingRole = (rs: KarsRole[]) => rs.some((r) => !NON_CASE_LEADING_ROLES.includes(r));

// ─── Role detection ─────────────────────────────────────────
export function useRoleView() {
  const authStore = useAuthStore();

  /** Alle normalisierten Rollen des Nutzers in Prioritätsreihenfolge, nie leer. */
  const roles = computed<KarsRole[]>(() => {
    // Light-Users (Beteiligte mit eingeschränktem Portal-Zugang) bekommen
    // dasselbe schlanke Section-Set wie Mandanten.
    if (authStore.user?.accessTier === "light") return ["mandant"];
    const orgRoles: string[] = authStore.organizationRoles ?? [];
    const present = new Set<KarsRole>();
    for (const r of orgRoles) {
      const k = ORG_ROLE_TO_KARS[r];
      if (k) present.add(k);
    }
    const ordered = ROLE_PRIORITY.filter((r) => present.has(r));
    return ordered.length > 0 ? ordered : ["other"];
  });

  /** Hauptrolle (permissivste), für Beschriftungen und Einzelwert-Konsumenten. */
  // `roles` ist nie leer; der Fallback befriedigt nur noUncheckedIndexedAccess.
  const role = computed<KarsRole>(() => roles.value[0] ?? "other");

  // ─── Section visibility: ODER über alle Rollen ────────────
  const visibleSections = computed(() => mergeVisibility(SECTION_VISIBILITY, roles.value));

  // ─── Key info fields: Vereinigung in Prioritätsreihenfolge ─
  const keyInfoFields = computed(() => mergeLists(KEY_INFO_CONFIG, roles.value, (f) => f.key));

  // ─── CTA config: Aktionen aller Rollen, Duplikate nach Label ─
  const ctaConfig = computed<CtaConfig | null>(() => {
    const actions = mergeLists(CTA_ACTIONS_BY_ROLE, roles.value, (a) => a.label);
    return actions.length > 0 ? { actions } : null;
  });

  // ─── Document checklist ─────────────────────────────────
  const requiredDocuments = computed(() => mergeLists(DOCUMENT_CHECKLIST, roles.value, (d) => d.key));

  // ─── Sub-status labels ──────────────────────────────────
  const subStatusLabels = computed(() => mergeLists(SUB_STATUS_CONFIG, roles.value, (s) => s.key));

  // ─── Convenience booleans: "hat diese Rolle" ────────────
  const has = (r: KarsRole) => roles.value.includes(r);
  const isAutohaus = computed(() => has("autohaus"));
  const isMietwagen = computed(() => has("mietwagen"));
  const isSachverstaendiger = computed(() => has("sachverstaendiger"));
  const isAnwalt = computed(() => has("anwalt"));
  const isMandant = computed(() => has("mandant"));
  /**
   * Ausschliesslich Mandant – keine weitere Rolle. Für Pflichten des Kunden
   * (z. B. die Aufforderung, den Unfallfragebogen auszufüllen): ein Betrieb,
   * der nebenbei auch Kundenrolle hat, wird damit nicht angestupst.
   */
  const isPureMandant = computed(() => roles.value.length === 1 && roles.value[0] === "mandant");
  const isAbschleppdienst = computed(() => has("abschleppdienst"));
  const isVersicherung = computed(() => has("versicherung"));
  const isFuhrpark = computed(() => has("fuhrpark"));
  const isVersicherungsmakler = computed(() => has("versicherungsmakler"));

  /** Versicherungskosten: Anwalt oder unklassifiziert. */
  const canSeeInsuranceCosts = computed(() => has("anwalt") || has("other"));
  /** Reparaturkosten: jede Rolle außer reinem Sachverständigen. */
  const canSeeRepairCosts = computed(() => roles.value.some((r) => r !== "sachverstaendiger"));
  /**
   * Status ändern / interne Notizen: nur fallführende Rollen
   * (siehe `NON_CASE_LEADING_ROLES`).
   */
  const canChangeStatus = computed(() => hasCaseLeadingRole(roles.value));
  const canSeeNotes = computed(() => hasCaseLeadingRole(roles.value));
  const hasFullAccess = computed(() => has("anwalt") || has("other"));

  /**
   * Schmerzensgeld und sonstige Personenschaden-Daten (Feedback 08.08.26, IV:
   * "Schmerzensgeld darf nur Anwalt und Kunde sehen!!!").
   *
   * Bewusst OHNE die Sammelrolle "other" – gefordert sind ausschliesslich
   * Anwalt und Kunde/Mandant. Werkstatt, Sachverstaendiger, Autovermietung
   * und alle uebrigen Rollen sehen diese Angaben nicht.
   *
   * WICHTIG: Diese Pruefung blendet nur die Oberflaeche aus. Sobald ein
   * Schmerzensgeld-Feld existiert, darf der Server es fuer andere Rollen
   * gar nicht erst ausliefern – clientseitiges Ausblenden allein ist kein
   * Schutz.
   */
  const canSeePainAndSuffering = computed(() => has("anwalt") || has("mandant"));

  return {
    role,
    roles,
    visibleSections,
    keyInfoFields,
    ctaConfig,
    requiredDocuments,
    subStatusLabels,
    isAutohaus,
    isMietwagen,
    isSachverstaendiger,
    isAnwalt,
    isMandant,
    isPureMandant,
    isAbschleppdienst,
    isVersicherung,
    isFuhrpark,
    isVersicherungsmakler,
    canSeeInsuranceCosts,
    canSeeRepairCosts,
    canChangeStatus,
    canSeeNotes,
    hasFullAccess,
    canSeePainAndSuffering,
  };
}

// ─── Merge-Helfer für Mehrfachrollen ─────────────────────────

/**
 * Vereinigt Listen mehrerer Rollen in Prioritätsreihenfolge, dedupliziert per
 * Schlüssel. Trifft derselbe Schlüssel mehrfach auf, gewinnt nicht einfach der
 * erste Eintrag: Boolean-Properties (Rechte wie `uploadableByRole`) werden per
 * ODER vereinigt, damit ein Recht aus einer zweiten Rolle nicht verloren geht.
 * Alle übrigen Properties (Label, Icon, …) stammen weiter vom höher
 * priorisierten Eintrag.
 */
export function mergeLists<T>(
  table: Record<KarsRole | "other", T[]>,
  rs: KarsRole[],
  keyOf: (item: T) => string,
): T[] {
  const indexByKey = new Map<string, number>();
  const out: T[] = [];
  for (const r of rs) {
    for (const item of table[r] ?? table.other ?? []) {
      const k = keyOf(item);
      const existingIndex = indexByKey.get(k);
      if (existingIndex === undefined) {
        indexByKey.set(k, out.length);
        out.push(item);
        continue;
      }
      const existing = out[existingIndex];
      if (existing !== undefined) out[existingIndex] = orBooleanProps(existing, item);
    }
  }
  return out;
}

/**
 * Verschmilzt zwei Einträge mit gleichem Schlüssel: Jede Property, die in
 * einem der beiden ein Boolean ist, wird zu `a || b`; alles andere bleibt bei
 * `a`. Gibt `a` unverändert zurück, wenn nichts zu vereinigen ist — und sonst
 * eine neue Kopie (keine Mutation der Konfigurationsobjekte).
 */
function orBooleanProps<T>(a: T, b: T): T {
  if (!a || !b || typeof a !== "object" || typeof b !== "object") return a;
  const source = a as Record<string, unknown>;
  let merged: Record<string, unknown> | null = null;
  for (const key of Object.keys(b as Record<string, unknown>)) {
    const av = source[key];
    const bv = (b as Record<string, unknown>)[key];
    if (typeof av !== "boolean" && typeof bv !== "boolean") continue;
    const next = Boolean(av) || Boolean(bv);
    if (next === av) continue;
    merged = merged ?? { ...source };
    merged[key] = next;
  }
  return (merged as T | null) ?? a;
}

/**
 * Sichtbare Bereiche der Fallansicht für beliebige Rollen (neue Map, ODER
 * über alle Rollen). Kundenfeedback 18.10.2026 (XXIII): Die Beteiligten-Karte
 * zeigt damit, was ein anderer Beteiligter in der Akte sieht – aus derselben
 * Tabelle wie die eigene Ansicht, ohne zweite Regel.
 */
export function sectionVisibilityForRoles(rs: readonly KarsRole[]): Record<string, boolean> {
  return mergeVisibility(SECTION_VISIBILITY, rs.length > 0 ? [...rs] : ["other"]);
}

/** ODER-Verknüpfung von Sichtbarkeits-Maps mehrerer Rollen. */
export function mergeVisibility(
  table: Record<KarsRole | "other", Record<string, boolean>>,
  rs: KarsRole[],
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const r of rs) {
    for (const [section, visible] of Object.entries(table[r] ?? table.other ?? {})) {
      out[section] = out[section] || visible;
    }
  }
  return out;
}

/** Cockpit-Kacheln als Vereinigung aller Rollen, in der Reihenfolge von COCKPIT_TILES_ALL. */
export function cockpitTilesForRoles(rs: KarsRole[]): readonly CockpitTileId[] {
  const allowed = new Set<CockpitTileId>();
  for (const r of rs) {
    for (const t of COCKPIT_TILES_BY_ROLE[r] ?? COCKPIT_TILES_ALL) allowed.add(t);
  }
  return COCKPIT_TILES_ALL.filter((t) => allowed.has(t));
}

// ─── Cockpit tile visibility per role ───────────────────────

/**
 * Welche Cockpit-Kacheln jede Rolle sieht. Reihenfolge/Position der Kacheln
 * bleibt im `tiles()`-Computed von SectionCockpit.vue; dieser Filter blendet
 * lediglich aus, was die Rolle nicht braucht.
 *
 * Für die Mietwagen-Rolle (Autovermietung) sind nur die vier Kacheln aus den
 * Konzept-docx-Specs relevant: Basisdaten, Beteiligte, Gegner, Ausfall/Mietwagen.
 */
export const COCKPIT_TILES_ALL = [
  "werte",
  "beteiligte",
  "neuanschaffung",
  "gegner",
  "ausfall",
  "reparatur",
  "basisdaten",
  "zahlungen",
  // Inhaber-Feedback 15.08.26 II.12: Beweismittel & Zeugen (Zeugen, Polizei,
  // Staatsanwaltschaft, erfasste Beweismittel) — sichtbar für Kanzlei und
  // Mandant/Kunde (beide nutzen COCKPIT_TILES_ALL).
  "beweismittel",
] as const;
export type CockpitTileId = (typeof COCKPIT_TILES_ALL)[number];

// Kasko-Kachelmenge: SectionCockpitKasko.vue (Spec 2026-09-19, §3) schneidet mit dieser Tabelle.
export const COCKPIT_TILES_BY_ROLE: Record<KarsRole | "other", readonly CockpitTileId[]> = {
  anwalt:            COCKPIT_TILES_ALL,
  sachverstaendiger: COCKPIT_TILES_ALL,
  autohaus:          COCKPIT_TILES_ALL,
  mandant:           COCKPIT_TILES_ALL,
  other:             COCKPIT_TILES_ALL,
  // Autovermietung: nur die vier docx-Tabs
  mietwagen: ["basisdaten", "beteiligte", "gegner", "ausfall"],
  // Abschleppdienst: absolutes Minimum lt. docs/fallansicht-redesign/
  // 02_berechtigungskonzept-abschleppunternehmen.md §2
  abschleppdienst: ["basisdaten", "zahlungen"],
  // Versicherung: Regulierungs-Fokus — Schadenwerte, Basisdaten, Zahlungen
  versicherung: ["werte", "basisdaten", "zahlungen"],
  // Fuhrpark: eigene Fahrzeuge, Reparatur/Werkstattrechnungen, Ausfall —
  // keine Schadenwerte-Kachel (enthält Sachverständigenkosten).
  fuhrpark: ["basisdaten", "beteiligte", "gegner", "reparatur", "ausfall", "zahlungen"],
  // Versicherungsmakler: wie Versicherung, aber ohne Zahlungen/Regulierung.
  versicherungsmakler: ["werte", "basisdaten", "beteiligte"],
};

// ─── Configuration objects ──────────────────────────────────

/**
 * Which navigation sections each role can see in the case detail.
 */
const SECTION_VISIBILITY: Record<KarsRole | "other", Record<string, boolean>> = {
  autohaus: {
    uebersicht: true,
    daten: true,
    zahlungen: true,
    dokumente: true,
    beteiligte: true,
    nachrichten: true,
    telefonate: true,
    kommunikation: true,
    historie: true,
    // Regressbereich (Inhaber-Feedback 15.08.26 II.3): nur Kanzlei.
    regress: false,
  },
  // Autovermietung: Fokus auf Anmietung/Ausfall. Zahlungen bleiben sichtbar
  // (das Backend liefert restringierten Rollen nur die eigenen Positionen),
  // aber interne Chronologie und Telefonate der Fallführung nicht.
  mietwagen: {
    uebersicht: true,
    daten: true,
    zahlungen: true,
    dokumente: true,
    beteiligte: true,
    nachrichten: true,
    telefonate: false,
    kommunikation: true,
    historie: false,
    // Regressbereich (Inhaber-Feedback 15.08.26 II.3): nur Kanzlei.
    regress: false,
  },
  // Abschleppdienst: strikte Daten-Minimierung lt. docs/fallansicht-redesign/
  // 02_berechtigungskonzept-abschleppunternehmen.md §2 — nur Übersicht,
  // Basisdaten und (eigene) Zahlungsposition.
  abschleppdienst: {
    uebersicht: true,
    daten: true,
    zahlungen: true,
    dokumente: false,
    beteiligte: false,
    nachrichten: false,
    telefonate: false,
    kommunikation: false,
    historie: false,
    // Regressbereich (Inhaber-Feedback 15.08.26 II.3): nur Kanzlei.
    regress: false,
  },
  // Versicherung: Regulierungs-Fokus. Keine Sicht auf Beteiligten-Details,
  // interne Telefonate oder die Fall-Historie der Kanzlei.
  versicherung: {
    uebersicht: true,
    daten: true,
    zahlungen: true,
    dokumente: true,
    beteiligte: false,
    nachrichten: true,
    telefonate: false,
    kommunikation: true,
    historie: false,
    // Regressbereich (Inhaber-Feedback 15.08.26 II.3): nur Kanzlei.
    regress: false,
  },
  // Fuhrpark: eigene Fahrzeuge und Werkstattrechnungen; keine interne
  // Chronologie/Telefonate der Fallführung.
  fuhrpark: {
    uebersicht: true,
    daten: true,
    zahlungen: true,
    dokumente: true,
    beteiligte: true,
    nachrichten: true,
    telefonate: false,
    kommunikation: true,
    historie: false,
  },
  // Versicherungsmakler: wie Versicherung, jedoch ohne Zahlungen
  // (keine Regulierungsrechte).
  versicherungsmakler: {
    uebersicht: true,
    daten: true,
    zahlungen: false,
    dokumente: true,
    beteiligte: false,
    nachrichten: true,
    telefonate: false,
    kommunikation: true,
    historie: false,
  },
  sachverstaendiger: {
    uebersicht: true,
    daten: true,
    zahlungen: false, // SV sieht keine Reparaturkosten
    dokumente: true,
    beteiligte: true,
    nachrichten: true,
    telefonate: true,
    kommunikation: true,
    historie: true,
    // Regressbereich (Inhaber-Feedback 15.08.26 II.3): nur Kanzlei.
    regress: false,
  },
  anwalt: {
    uebersicht: true,
    daten: true,
    zahlungen: true,
    dokumente: true,
    beteiligte: true,
    nachrichten: true,
    telefonate: true,
    kommunikation: true,
    historie: true,
    // Regressbereich (Inhaber-Feedback 15.08.26 II.3): nur Kanzlei.
    regress: true,
  },
  mandant: {
    uebersicht: true,
    daten: false,
    zahlungen: false,
    dokumente: true,
    beteiligte: false,
    nachrichten: true,
    telefonate: false,
    kommunikation: true,
    historie: false,
    // Regressbereich (Inhaber-Feedback 15.08.26 II.3): nur Kanzlei.
    regress: false,
  },
  other: {
    uebersicht: true,
    daten: true,
    zahlungen: true,
    dokumente: true,
    beteiligte: true,
    nachrichten: true,
    telefonate: true,
    kommunikation: true,
    historie: true,
    // Regressbereich (Inhaber-Feedback 15.08.26 II.3): nur Kanzlei.
    regress: true,
  },
};

/**
 * The "10 key info" fields for each role. Each entry maps to a field key
 * that components can resolve from the case data.
 */
export interface KeyInfoField {
  key: string;
  label: string;
  icon: string; // lucide icon name
  section?: string; // source data section hint
}

const KEY_INFO_CONFIG: Record<KarsRole | "other", KeyInfoField[]> = {
  autohaus: [
    { key: "caseNumber", label: gtKey("Aktenzeichen"), icon: "FileText" },
    { key: "status", label: gtKey("Fallstatus"), icon: "Activity" },
    { key: "accidentDate", label: gtKey("Unfalldatum"), icon: "CalendarDays" },
    { key: "vehiclePlate", label: gtKey("Kennzeichen"), icon: "Car" },
    { key: "vehicleModel", label: gtKey("Fahrzeug"), icon: "Car" },
    { key: "repairStatus", label: gtKey("Reparaturstatus"), icon: "Wrench" },
    { key: "clientName", label: gtKey("Kunde"), icon: "User" },
    { key: "opponentInsurance", label: gtKey("Gegn. Versicherung"), icon: "Shield" },
    { key: "gutachtenStatus", label: gtKey("Gutachten"), icon: "ClipboardCheck" },
    { key: "createdAt", label: gtKey("Angelegt"), icon: "Clock" },
  ],
  mietwagen: [
    { key: "caseNumber", label: gtKey("Aktenzeichen"), icon: "FileText" },
    { key: "status", label: gtKey("Fallstatus"), icon: "Activity" },
    { key: "accidentDate", label: gtKey("Unfalldatum"), icon: "CalendarDays" },
    { key: "vehiclePlate", label: gtKey("Kennzeichen"), icon: "Car" },
    { key: "clientName", label: gtKey("Kunde"), icon: "User" },
    { key: "opponentInsurance", label: gtKey("Gegn. Versicherung"), icon: "Shield" },
    { key: "liabilityStatus", label: gtKey("Quote anerkannt"), icon: "Scale" },
    { key: "rentalCarStatus", label: gtKey("Mietwagen"), icon: "KeyRound" },
    { key: "lossOfUseStatus", label: gtKey("Nutzungsausfall"), icon: "CarOff" },
    { key: "createdAt", label: gtKey("Angelegt"), icon: "Clock" },
  ],
  sachverstaendiger: [
    { key: "caseNumber", label: gtKey("Aktenzeichen"), icon: "FileText" },
    { key: "status", label: gtKey("Fallstatus"), icon: "Activity" },
    { key: "accidentDate", label: gtKey("Unfalldatum"), icon: "CalendarDays" },
    { key: "vehiclePlate", label: gtKey("Kennzeichen"), icon: "Car" },
    { key: "vehicleModel", label: gtKey("Fahrzeug"), icon: "Car" },
    { key: "vehicleVIN", label: gtKey("Fahrgestellnr."), icon: "Hash" },
    { key: "vehicleMileage", label: gtKey("Km-Stand"), icon: "Gauge" },
    { key: "clientName", label: gtKey("Kunde"), icon: "User" },
    { key: "gutachtenStatus", label: gtKey("Gutachten"), icon: "ClipboardCheck" },
    { key: "inspectionDate", label: gtKey("Besichtigungstermin"), icon: "CalendarClock" },
  ],
  anwalt: [
    { key: "caseNumber", label: gtKey("Aktenzeichen"), icon: "FileText" },
    { key: "status", label: gtKey("Fallstatus"), icon: "Activity" },
    { key: "accidentDate", label: gtKey("Unfalldatum"), icon: "CalendarDays" },
    { key: "clientName", label: gtKey("Kunde"), icon: "User" },
    { key: "opponentName", label: gtKey("Unfallgegner"), icon: "Users" },
    { key: "opponentInsurance", label: gtKey("Gegn. Versicherung"), icon: "Shield" },
    { key: "legalExpense", label: gtKey("Rechtsschutz"), icon: "ShieldCheck" },
    { key: "liabilityStatus", label: gtKey("Haftungslage"), icon: "Scale" },
    { key: "claimAmount", label: gtKey("Schadenhöhe"), icon: "Euro" },
    { key: "gutachtenStatus", label: gtKey("Gutachten"), icon: "ClipboardCheck" },
  ],
  mandant: [
    { key: "caseNumber", label: gtKey("Aktenzeichen"), icon: "FileText" },
    { key: "status", label: gtKey("Status"), icon: "Activity" },
    { key: "accidentDate", label: gtKey("Unfalldatum"), icon: "CalendarDays" },
    { key: "vehiclePlate", label: gtKey("Kennzeichen"), icon: "Car" },
    { key: "repairStatus", label: gtKey("Reparatur"), icon: "Wrench" },
    { key: "gutachtenStatus", label: gtKey("Gutachten"), icon: "ClipboardCheck" },
    { key: "questionnaireStatus", label: gtKey("Fragebogen"), icon: "ClipboardList" },
    { key: "nextStep", label: gtKey("Nächster Schritt"), icon: "ArrowRight" },
  ],
  abschleppdienst: [
    { key: "caseNumber", label: gtKey("Aktenzeichen"), icon: "FileText" },
    { key: "status", label: gtKey("Fallstatus"), icon: "Activity" },
    { key: "accidentDate", label: gtKey("Unfalldatum"), icon: "CalendarDays" },
    { key: "vehiclePlate", label: gtKey("Kennzeichen"), icon: "Car" },
    { key: "vehicleModel", label: gtKey("Fahrzeug"), icon: "Car" },
    { key: "liabilityStatus", label: gtKey("Quote anerkannt"), icon: "Scale" },
    { key: "createdAt", label: gtKey("Angelegt"), icon: "Clock" },
  ],
  versicherung: [
    { key: "caseNumber", label: gtKey("Aktenzeichen"), icon: "FileText" },
    { key: "status", label: gtKey("Fallstatus"), icon: "Activity" },
    { key: "accidentDate", label: gtKey("Unfalldatum"), icon: "CalendarDays" },
    { key: "vehiclePlate", label: gtKey("Kennzeichen"), icon: "Car" },
    { key: "liabilityStatus", label: gtKey("Haftungslage"), icon: "Scale" },
    { key: "claimAmount", label: gtKey("Schadenhöhe"), icon: "Euro" },
    { key: "gutachtenStatus", label: gtKey("Gutachten"), icon: "ClipboardCheck" },
    { key: "createdAt", label: gtKey("Angelegt"), icon: "Clock" },
  ],
  fuhrpark: [
    { key: "caseNumber", label: "Aktenzeichen", icon: "FileText" },
    { key: "status", label: "Fallstatus", icon: "Activity" },
    { key: "accidentDate", label: "Unfalldatum", icon: "CalendarDays" },
    { key: "vehiclePlate", label: "Kennzeichen", icon: "Car" },
    { key: "vehicleModel", label: "Fahrzeug", icon: "Car" },
    { key: "repairStatus", label: "Reparaturstatus", icon: "Wrench" },
    { key: "opponentInsurance", label: "Gegn. Versicherung", icon: "Shield" },
    { key: "liabilityStatus", label: "Haftungslage", icon: "Scale" },
    { key: "createdAt", label: "Angelegt", icon: "Clock" },
  ],
  versicherungsmakler: [
    { key: "caseNumber", label: "Aktenzeichen", icon: "FileText" },
    { key: "status", label: "Fallstatus", icon: "Activity" },
    { key: "accidentDate", label: "Unfalldatum", icon: "CalendarDays" },
    { key: "clientName", label: "Kunde", icon: "User" },
    { key: "vehiclePlate", label: "Kennzeichen", icon: "Car" },
    { key: "opponentInsurance", label: "Gegn. Versicherung", icon: "Shield" },
    { key: "liabilityStatus", label: "Haftungslage", icon: "Scale" },
    { key: "gutachtenStatus", label: "Gutachten", icon: "ClipboardCheck" },
    { key: "createdAt", label: "Angelegt", icon: "Clock" },
  ],
  other: [
    { key: "caseNumber", label: gtKey("Aktenzeichen"), icon: "FileText" },
    { key: "status", label: gtKey("Fallstatus"), icon: "Activity" },
    { key: "accidentDate", label: gtKey("Unfalldatum"), icon: "CalendarDays" },
    { key: "clientName", label: gtKey("Kunde"), icon: "User" },
    { key: "vehiclePlate", label: gtKey("Kennzeichen"), icon: "Car" },
    { key: "opponentInsurance", label: gtKey("Gegn. Versicherung"), icon: "Shield" },
    { key: "gutachtenStatus", label: gtKey("Gutachten"), icon: "ClipboardCheck" },
    { key: "createdAt", label: gtKey("Angelegt"), icon: "Clock" },
  ],
};

/**
 * CTA (Call to Action) configuration per role.
 * `condition` is a function that receives case + document data to decide visibility.
 */
export interface CtaAction {
  label: string;
  description: string;
  icon: string;
  color: string;
  actionType: "upload" | "navigate" | "status" | "link";
  target?: string;
}

export interface CtaConfig {
  actions: CtaAction[];
}

const CTA_CONFIG: Record<KarsRole | "other", CtaConfig> = {
  sachverstaendiger: {
    actions: [
      {
        label: gtKey("Gutachten hochladen"),
        description: gtKey("Laden Sie das fertige Gutachten als PDF hoch."),
        icon: "Upload",
        color: "primary",
        actionType: "navigate",
        target: "dokumente",
      },
      {
        label: gtKey("SV-Rechnung einreichen"),
        description: gtKey("Reichen Sie Ihre Sachverständigenrechnung ein."),
        icon: "Receipt",
        color: "primary",
        actionType: "navigate",
        target: "dokumente",
      },
    ],
  },
  anwalt: {
    actions: [
      {
        label: gtKey("Schaden beziffern"),
        description: gtKey("Erfassen Sie die Schadenshöhe und machen Sie die Ansprüche geltend."),
        icon: "Calculator",
        color: "primary",
        actionType: "navigate",
        target: "zahlungen",
      },
      {
        label: gtKey("Versicherung kontaktieren"),
        description: gtKey("Senden Sie eine Nachricht an die gegnerische Versicherung."),
        icon: "Send",
        color: "primary",
        actionType: "navigate",
        target: "nachrichten",
      },
    ],
  },
  autohaus: {
    actions: [
      {
        label: gtKey("Reparaturrechnung hochladen"),
        description: gtKey("Laden Sie die Werkstattrechnung zur Übersicht hoch."),
        icon: "Upload",
        color: "primary",
        actionType: "navigate",
        target: "dokumente",
      },
      {
        label: gtKey("Reparaturstatus aktualisieren"),
        description: gtKey("Aktualisieren Sie den Fortschritt der Fahrzeugreparatur."),
        icon: "Wrench",
        color: "primary",
        actionType: "navigate",
        target: "zahlungen",
      },
    ],
  },
  mietwagen: {
    actions: [
      {
        label: gtKey("Mietwagenrechnung hochladen"),
        description: gtKey("Laden Sie die Mietwagenrechnung als PDF hoch."),
        icon: "Upload",
        color: "primary",
        actionType: "navigate",
        target: "dokumente",
      },
      {
        label: gtKey("Mietzeitraum aktualisieren"),
        description: gtKey("Pflegen Sie Mietwagengruppe, Mietzeitraum und Abrechnungsweg."),
        icon: "KeyRound",
        color: "primary",
        actionType: "navigate",
        target: "daten",
      },
    ],
  },
  mandant: {
    actions: [
      {
        label: gtKey("Unfallfragebogen ausfüllen"),
        description: gtKey("Bitte füllen Sie den Fragebogen vollständig aus, damit Ihr Fall bearbeitet werden kann."),
        icon: "ClipboardList",
        color: "primary",
        actionType: "link",
        target: "fragebogen",
      },
    ],
  },
  abschleppdienst: {
    actions: [
      {
        label: gtKey("Abschlepprechnung einreichen"),
        description: gtKey("Reichen Sie Ihre Abschlepprechnung zur Regulierung ein."),
        icon: "Upload",
        color: "primary",
        actionType: "navigate",
        target: "zahlungen",
      },
    ],
  },
  versicherung: {
    actions: [
      {
        label: gtKey("Zahlungsübersicht öffnen"),
        description: gtKey("Prüfen Sie die eingereichten Positionen zur Regulierung."),
        icon: "Banknote",
        color: "primary",
        actionType: "navigate",
        target: "zahlungen",
      },
    ],
  },
  fuhrpark: {
    actions: [
      {
        label: "Fahrzeugdaten prüfen",
        description: "Prüfen und ergänzen Sie die Daten Ihres Fahrzeugs.",
        icon: "Car",
        color: "primary",
        actionType: "navigate",
        target: "daten",
      },
      {
        label: "Werkstattrechnung hochladen",
        description: "Laden Sie die Reparaturrechnung Ihrer Werkstatt hoch.",
        icon: "Upload",
        color: "primary",
        actionType: "navigate",
        target: "dokumente",
      },
    ],
  },
  versicherungsmakler: {
    actions: [
      {
        label: "Unterlagen einreichen",
        description: "Reichen Sie Versicherungsschreiben und Unterlagen Ihres Kunden ein.",
        icon: "Upload",
        color: "primary",
        actionType: "navigate",
        target: "dokumente",
      },
    ],
  },
  other: {
    actions: [],
  },
};

/**
 * Flache Rolle→Aktionen-Tabelle für `mergeLists`. Einmalig beim Modul-Laden
 * gebaut statt bei jeder Neuberechnung des CTA-Computeds.
 */
const CTA_ACTIONS_BY_ROLE = Object.fromEntries(
  (Object.keys(CTA_CONFIG) as (KarsRole | "other")[]).map((k) => [k, CTA_CONFIG[k]?.actions ?? []]),
) as Record<KarsRole | "other", CtaAction[]>;

/**
 * Document types that each role expects to see / upload.
 */
export interface RequiredDocument {
  key: string;
  label: string;
  icon: string;
  category: string; // matches document category value
  uploadableByRole: boolean; // true if this role can upload it
}

const DOCUMENT_CHECKLIST: Record<KarsRole | "other", RequiredDocument[]> = {
  autohaus: [
    { key: "gutachten", label: gtKey("Gutachten"), icon: "FileSearch", category: "gutachten", uploadableByRole: false },
    { key: "reparaturrechnung", label: gtKey("Reparaturrechnung"), icon: "Receipt", category: "rechnung", uploadableByRole: true },
    { key: "polizeibericht", label: gtKey("Polizeibericht"), icon: "FileWarning", category: "polizeibericht", uploadableByRole: false },
    // Kundenfeedback 16.10.2026 Teil 2 (Entscheidung a): Fahrzeug-/Schadenfotos
    // der Werkstatt gehören zu den Unterlagen Fahrzeugschaden, nicht zu den
    // Beweismitteln (Lichtbilder der Unfallsituation, nur Kanzlei und Kunde).
    { key: "foto", label: gtKey("Fahrzeugfotos"), icon: "Camera", category: "schadenfoto", uploadableByRole: true },
  ],
  mietwagen: [
    { key: "gutachten", label: gtKey("Gutachten"), icon: "FileSearch", category: "gutachten", uploadableByRole: false },
    { key: "mietwagenrechnung", label: gtKey("Mietwagenrechnung"), icon: "Receipt", category: "rechnung", uploadableByRole: true },
    { key: "mietvertrag", label: gtKey("Mietvertrag"), icon: "FileSignature", category: "vertrag", uploadableByRole: true },
  ],
  sachverstaendiger: [
    { key: "gutachten", label: gtKey("Gutachten"), icon: "FileSearch", category: "gutachten", uploadableByRole: true },
    { key: "sv_rechnung", label: gtKey("SV-Rechnung"), icon: "Receipt", category: "rechnung", uploadableByRole: true },
    // Kundenfeedback 16.10.2026 Teil 2 (Entscheidung a): Schadenfotos des Fahrzeugs.
    { key: "foto", label: gtKey("Fahrzeugfotos"), icon: "Camera", category: "schadenfoto", uploadableByRole: true },
  ],
  anwalt: [
    { key: "gutachten", label: gtKey("Gutachten"), icon: "FileSearch", category: "gutachten", uploadableByRole: false },
    { key: "vollmacht", label: gtKey("Vollmacht"), icon: "FileSignature", category: "vollmacht", uploadableByRole: true },
    { key: "polizeibericht", label: gtKey("Polizeibericht"), icon: "FileWarning", category: "polizeibericht", uploadableByRole: false },
    { key: "versicherungsschreiben", label: gtKey("Versicherungsschreiben"), icon: "Mail", category: "versicherungsschrift", uploadableByRole: true },
    { key: "reparaturrechnung", label: gtKey("Reparaturrechnung"), icon: "Receipt", category: "rechnung", uploadableByRole: false },
    { key: "aerztl_bericht", label: gtKey("Ärztl. Bericht"), icon: "Heart", category: "aerztlicherbericht", uploadableByRole: false },
  ],
  mandant: [
    { key: "fragebogen", label: gtKey("Fragebogen ausgefüllt"), icon: "ClipboardCheck", category: "_fragebogen", uploadableByRole: false },
    { key: "foto", label: gtKey("Unfallfotos"), icon: "Camera", category: "foto", uploadableByRole: true },
  ],
  abschleppdienst: [
    { key: "abschlepprechnung", label: gtKey("Abschlepprechnung"), icon: "Receipt", category: "rechnung", uploadableByRole: true },
  ],
  versicherung: [
    { key: "gutachten", label: gtKey("Gutachten"), icon: "FileSearch", category: "gutachten", uploadableByRole: false },
    { key: "versicherungsschreiben", label: gtKey("Versicherungsschreiben"), icon: "Mail", category: "versicherungsschrift", uploadableByRole: true },
    { key: "reparaturrechnung", label: gtKey("Reparaturrechnung"), icon: "Receipt", category: "rechnung", uploadableByRole: false },
  ],
  fuhrpark: [
    { key: "gutachten", label: "Gutachten", icon: "FileSearch", category: "gutachten", uploadableByRole: false },
    { key: "reparaturrechnung", label: "Werkstattrechnung", icon: "Receipt", category: "rechnung", uploadableByRole: true },
    { key: "foto", label: "Unfallfotos", icon: "Camera", category: "foto", uploadableByRole: true },
  ],
  versicherungsmakler: [
    { key: "gutachten", label: "Gutachten", icon: "FileSearch", category: "gutachten", uploadableByRole: false },
    { key: "versicherungsschreiben", label: "Versicherungsschreiben", icon: "Mail", category: "versicherungsschrift", uploadableByRole: true },
  ],
  other: [
    { key: "gutachten", label: gtKey("Gutachten"), icon: "FileSearch", category: "gutachten", uploadableByRole: false },
    { key: "vollmacht", label: gtKey("Vollmacht"), icon: "FileSignature", category: "vollmacht", uploadableByRole: false },
    { key: "polizeibericht", label: gtKey("Polizeibericht"), icon: "FileWarning", category: "polizeibericht", uploadableByRole: false },
    { key: "reparaturrechnung", label: gtKey("Reparaturrechnung"), icon: "Receipt", category: "rechnung", uploadableByRole: false },
  ],
};

/**
 * Sub-status labels that appear under the main case status per role.
 */
export interface SubStatusEntry {
  key: string;
  label: string;
  icon: string;
  /** Determines this sub-status state from case data */
  resolveState: "pending" | "active" | "done";
}

const SUB_STATUS_CONFIG: Record<KarsRole | "other", SubStatusEntry[]> = {
  autohaus: [
    { key: "gutachtenErstellt", label: gtKey("Gutachten erstellt"), icon: "FileSearch", resolveState: "pending" },
    { key: "reparaturBegonnen", label: gtKey("Reparatur begonnen"), icon: "Wrench", resolveState: "pending" },
    { key: "reparaturAbgeschlossen", label: gtKey("Reparatur abgeschlossen"), icon: "CheckCircle2", resolveState: "pending" },
    { key: "rechnungEingereicht", label: gtKey("Rechnung eingereicht"), icon: "Receipt", resolveState: "pending" },
  ],
  mietwagen: [
    { key: "mietwagenBenoetigt", label: gtKey("Mietwagen benötigt"), icon: "KeyRound", resolveState: "pending" },
    { key: "mietzeitraumErfasst", label: gtKey("Mietzeitraum erfasst"), icon: "CalendarDays", resolveState: "pending" },
    { key: "rechnungEingereicht", label: gtKey("Rechnung eingereicht"), icon: "Receipt", resolveState: "pending" },
    { key: "regulierung", label: gtKey("Regulierung"), icon: "Banknote", resolveState: "pending" },
  ],
  sachverstaendiger: [
    { key: "besichtigung", label: gtKey("Besichtigung"), icon: "Eye", resolveState: "pending" },
    { key: "gutachtenErstellt", label: gtKey("Gutachten erstellt"), icon: "FileSearch", resolveState: "pending" },
    { key: "gutachtenHochgeladen", label: gtKey("Gutachten hochgeladen"), icon: "Upload", resolveState: "pending" },
    { key: "svRechnungEingereicht", label: gtKey("SV-Rechnung eingereicht"), icon: "Receipt", resolveState: "pending" },
  ],
  anwalt: [
    { key: "vollmachtErteilt", label: gtKey("Vollmacht erteilt"), icon: "FileSignature", resolveState: "pending" },
    { key: "haftungspruefung", label: gtKey("Haftungsprüfung"), icon: "Scale", resolveState: "pending" },
    { key: "schadenBeziffert", label: gtKey("Schaden beziffert"), icon: "Calculator", resolveState: "pending" },
    { key: "anspruchGeltend", label: gtKey("Anspruch geltend gemacht"), icon: "Send", resolveState: "pending" },
    { key: "regulierung", label: gtKey("Regulierung"), icon: "Banknote", resolveState: "pending" },
  ],
  mandant: [
    { key: "fragebogenAusgefuellt", label: gtKey("Fragebogen ausgefüllt"), icon: "ClipboardList", resolveState: "pending" },
    { key: "gutachtenErstellt", label: gtKey("Gutachten erstellt"), icon: "FileSearch", resolveState: "pending" },
    { key: "reparatur", label: gtKey("Reparatur"), icon: "Wrench", resolveState: "pending" },
    { key: "abgeschlossen", label: gtKey("Fall abgeschlossen"), icon: "CheckCircle2", resolveState: "pending" },
  ],
  abschleppdienst: [
    { key: "fahrzeugGeborgen", label: gtKey("Fahrzeug abgeschleppt"), icon: "Truck", resolveState: "pending" },
    { key: "rechnungEingereicht", label: gtKey("Rechnung eingereicht"), icon: "Receipt", resolveState: "pending" },
    { key: "regulierung", label: gtKey("Regulierung"), icon: "Banknote", resolveState: "pending" },
  ],
  versicherung: [
    { key: "gutachtenErstellt", label: gtKey("Gutachten erstellt"), icon: "FileSearch", resolveState: "pending" },
    { key: "schadenBeziffert", label: gtKey("Schaden beziffert"), icon: "Calculator", resolveState: "pending" },
    { key: "regulierung", label: gtKey("Regulierung"), icon: "Banknote", resolveState: "pending" },
  ],
  fuhrpark: [
    { key: "gutachtenErstellt", label: "Gutachten erstellt", icon: "FileSearch", resolveState: "pending" },
    { key: "reparatur", label: "Reparatur", icon: "Wrench", resolveState: "pending" },
    { key: "rechnungEingereicht", label: "Rechnung eingereicht", icon: "Receipt", resolveState: "pending" },
    { key: "regulierung", label: "Regulierung", icon: "Banknote", resolveState: "pending" },
  ],
  versicherungsmakler: [
    { key: "gutachtenErstellt", label: "Gutachten erstellt", icon: "FileSearch", resolveState: "pending" },
    { key: "schadenBeziffert", label: "Schaden beziffert", icon: "Calculator", resolveState: "pending" },
    { key: "regulierung", label: "Regulierung", icon: "Banknote", resolveState: "pending" },
  ],
  other: [
    { key: "gutachtenErstellt", label: gtKey("Gutachten erstellt"), icon: "FileSearch", resolveState: "pending" },
    { key: "reparatur", label: gtKey("Reparatur"), icon: "Wrench", resolveState: "pending" },
    { key: "regulierung", label: gtKey("Regulierung"), icon: "Banknote", resolveState: "pending" },
  ],
};
